import { useState } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Loader2, Mic, MicOff } from "lucide-react";
import { IMAGE_CATEGORIES, startSpeechRecognition } from "./helpers";

export const ImageUploadDialog = ({ open, onOpenChange, orderId, preview, onUploaded }) => {
  const [category, setCategory] = useState("sonstiges");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const handleClose = (val) => {
    if (!val) {
      setCategory("sonstiges");
      setDescription("");
    }
    onOpenChange(val);
  };

  const handleUpload = async () => {
    if (!preview?.file || !orderId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", preview.file);
      formData.append("category", category);
      if (description.trim()) formData.append("description", description.trim());
      await api.post(`/api/work-orders/${orderId}/images`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Bild hochgeladen");
      handleClose(false);
      onUploaded?.();
    } catch {
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="detail-image-upload-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>Foto hinzufügen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {preview && (
            <div className="aspect-video bg-muted rounded-lg overflow-hidden">
              <img src={preview.url} alt="Vorschau" className="w-full h-full object-contain" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Kategorie</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {IMAGE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Beschreibung (optional)</Label>
            <div className="flex gap-2">
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="z.B. Kratzer an der Fahrertür" />
              <Button type="button" variant="outline" size="icon"
                onClick={() => startSpeechRecognition(setDescription, description, setIsListening)}
                title="Spracheingabe">
                {isListening ? <MicOff className="w-4 h-4 text-red-500" /> : <Mic className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>Abbrechen</Button>
            <Button onClick={handleUpload} disabled={uploading || !preview} data-testid="detail-image-upload-submit">
              {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Hochladen...</>) : (<><Upload className="w-4 h-4 mr-2" />Hochladen</>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
